import { useCallback } from 'react'
import * as entityLoaders from 'modules/Cube/communication/external/billingSchedule.api.v1/ports/entityLoaders'
import * as commonEntityLoaders from 'modules/Cube/communication/external/common.api.v1/ports/entityLoaders'

import { useNotifications } from '@sequencehq/core-components'
import {
  NEW_PHASE_PREFIX,
  NEW_ROOT_ENTITY_PREFIX
} from 'modules/Cube/domain/cube.constants'
import { apiDatesAdapters } from '@sequencehq/utils/dates'
import { add } from '@sequencehq/utils/dates'
import { v1ApiPrice } from 'modules/Cube/communication/external/common.api.v1/ports/entityLoaders/useLoadPrices'
import { v1ApiProduct } from 'modules/Cube/communication/external/common.api.v1/ports/entityLoaders/useLoadProducts'
import { v1ApiIntegration } from 'modules/Cube/communication/external/common.api.v1/ports/entityLoaders/useLoadIntegrations'
import {
  v1ApiBillingSchedule,
  v1ApiBillingScheduleSettings
} from 'modules/Cube/communication/external/billingSchedule.api.v1/ports/entityLoaders/useLoadBillingSchedule'
import { v1ApiTaxRate } from 'modules/Cube/communication/external/billingSchedule.api.v1/ports/entityLoaders/useLoadSingleTaxRate'
import { Currency, fetchQuery } from '@sequencehq/api'
import {
  CorePortErrors,
  CubePortImplementationProp
} from 'modules/Cube/domain/cube.domain.types.ts'
import { dashboardv99990101Client } from '@sequencehq/api/dashboard/v99990101'
import {
  type BillingSettings,
  dashboard20240730Client
} from '@sequencehq/api/dashboard/v20240730'
import { apiQueryClient } from 'features/api/apiQueryClient'
import { MINUTES } from '@sequencehq/utils/dates'
import { ApiCustomer } from '../../common.api.v1/ports/entityLoaders/useLoadSingleCustomer'

export type LoadedBillingScheduleData = {
  billingSchedule: Omit<
    v1ApiBillingSchedule,
    'status' | 'isDraft' | 'prices'
  > & {
    status: v1ApiBillingSchedule['status'] | 'NEW'
  }
  billingScheduleSettings?: {
    paymentProvider: v1ApiBillingScheduleSettings['paymentProvider']
    autoCharge: v1ApiBillingScheduleSettings['autoCharge']
  }
  prices: v1ApiPrice[]
  products: v1ApiProduct[]
  taxRates: v1ApiTaxRate[]
  customers: ApiCustomer[]
  integrations: v1ApiIntegration[]
  enabledCurrencies: Currency[]
  billingSettings: BillingSettings | null
}

/**
 * The account's billing settings drive a couple of the editor's defaults, and
 * both the new-schedule and existing-schedule paths need them. They change very
 * rarely, so we read them through the shared query cache rather than fetching
 * again for every load.
 *
 * A failure here is swallowed on purpose: these are only defaults, and losing
 * them is not a good enough reason to stop the whole editor loading.
 */
const loadBillingSettings = async (): Promise<BillingSettings | null> => {
  try {
    return await fetchQuery(
      apiQueryClient,
      dashboard20240730Client.getBillingSettings,
      undefined,
      { staleTime: 5 * MINUTES }
    )
  } catch {
    return null
  }
}

type UseLoadBillingScheduleEditor = () => (ctx: CubePortImplementationProp) => (
  billingScheduleId?: string
) => Promise<{
  data: LoadedBillingScheduleData | null
  error: CorePortErrors | null
}>

/**
 * This hook is used for the initial load of critical data for the editor, such
 * as the billing schedule and associated settings. Note that we do not need to
 * load information such as prices in the critical path, as these are 'visual
 * enhancements' which are handled by their relevant components.
 */
export const useLoadBillingScheduleEditor: UseLoadBillingScheduleEditor =
  () => {
    const { displayNotification } = useNotifications()
    /**
     * We have a fair bit to load, and it can be split into multiple phases - the
     * first is loading the billing schedule itself, and we will then 'hydrate' the
     * ids in the schedule.
     */
    const pricesLoader = commonEntityLoaders.useLoadPrices()
    const productsLoader = commonEntityLoaders.useLoadProducts()
    const customerLoader = commonEntityLoaders.useLoadSingleCustomer()
    const integrationsLoader = commonEntityLoaders.useLoadIntegrations()
    const enabledCurrenciesLoader =
      commonEntityLoaders.useLoadEnabledCurrencies()

    const billingScheduleLoader = entityLoaders.useLoadBillingSchedule()
    const allTaxRatesLoader = entityLoaders.useLoadAllTaxRates()

    /**
     * Loads data for a billing schedule, or returns a
     * default 'new' billing schedule
     */
    const loadBillingSchedule = useCallback(
      (ctx: CubePortImplementationProp) =>
        async (
          billingScheduleId?: string
        ): Promise<{
          data: Pick<
            LoadedBillingScheduleData,
            'billingSchedule' | 'billingScheduleSettings'
          > | null
          error: CorePortErrors | null
        }> => {
          if (!billingScheduleId) {
            const defaultAutoChargeSetting =
              await dashboardv99990101Client.getStripeCollectionsSettings()

            const shouldAutoEnableStripePayments = Boolean(
              defaultAutoChargeSetting?.data?.autoEnableStripePayments
            )

            const billingSettings = await loadBillingSettings()

            const shouldAutoIssueInvoices =
              billingSettings?.autoIssueInvoices === 'ALL'

            return Promise.resolve({
              data: {
                billingSchedule: {
                  id: `${NEW_ROOT_ENTITY_PREFIX}${crypto.randomUUID()}`,
                  status: 'NEW',
                  type: 'CUSTOM',
                  startDate: apiDatesAdapters.toApi(new Date()),
                  endDate: apiDatesAdapters.toApi(
                    add(new Date(), { years: 1, days: -1 })
                  ),
                  autoIssueInvoices: shouldAutoIssueInvoices,
                  recurrenceDayOfMonth: 1,
                  phases: [
                    {
                      id: `${NEW_PHASE_PREFIX}${crypto.randomUUID()}`,
                      startDate: apiDatesAdapters.toApi(new Date()),
                      endDate: apiDatesAdapters.toApi(
                        add(new Date(), { years: 1, days: -1 })
                      ),
                      priceIds: [],
                      discounts: [],
                      minimums: [],
                      recurrencePreference:
                        'CONTINUE_FROM_PREVIOUS_PHASE' as const,
                      phasePriceMetadata: []
                    }
                  ],
                  customerId: ctx?.defaultValues?.customerId ?? '',
                  purchaseOrderNumber: '',
                  taxRates: [],
                  rollUpBilling: false,
                  createdAt: new Date().toISOString(),
                  attachmentAssets: [],
                  integrationIds: []
                } as LoadedBillingScheduleData['billingSchedule'],
                billingScheduleSettings: {
                  paymentProvider: shouldAutoEnableStripePayments
                    ? 'STRIPE'
                    : 'NONE',
                  autoCharge: Boolean(
                    defaultAutoChargeSetting?.data?.enableAutomaticPayments
                  )
                }
              },
              error: null
            })
          }

          return billingScheduleLoader(billingScheduleId)
        },
      [billingScheduleLoader]
    )

    const load = useCallback(
      (ctx: CubePortImplementationProp) =>
        async (billingScheduleId?: string) => {
          try {
            const loadedBillingSchedule =
              await loadBillingSchedule(ctx)(billingScheduleId)

            if (!loadedBillingSchedule.data || loadedBillingSchedule.error) {
              throw new Error("Couldn't load billing schedule")
            }

            const { billingSchedule, billingScheduleSettings } =
              loadedBillingSchedule.data

            const allPriceIds =
              billingSchedule.phases?.flatMap(({ priceIds }) => priceIds) ?? []

            const [
              integrationsLoaderData,
              pricesLoaderData,
              customerLoaderData,
              allTaxRatesLoaderData,
              enabledCurrencies,
              billingSettings
            ] = await Promise.all([
              integrationsLoader(),
              billingScheduleId
                ? pricesLoader({ priceIds: allPriceIds })
                : Promise.resolve({ prices: [] }),
              billingSchedule.customerId
                ? customerLoader({
                    customerId: billingSchedule.customerId
                  })
                : Promise.resolve(null),
              allTaxRatesLoader(),
              enabledCurrenciesLoader(),
              loadBillingSettings()
            ])

            const productsLoaderData = await productsLoader({
              prices: pricesLoaderData.prices
            })

            return {
              data: {
                billingSchedule,
                billingScheduleSettings,
                prices: pricesLoaderData.prices,
                products: [...productsLoaderData.products],
                taxRates: allTaxRatesLoaderData,
                customers: [customerLoaderData].filter((c): c is ApiCustomer =>
                  Boolean(c)
                ),
                integrations: integrationsLoaderData.integrations,
                enabledCurrencies,
                billingSettings
              },
              error: null
            }
          } catch {
            displayNotification('Could not load billing schedule', {
              type: 'error'
            })
            return {
              data: null,
              error: CorePortErrors.Other
            }
          }
        },
      [
        loadBillingSchedule,
        integrationsLoader,
        pricesLoader,
        customerLoader,
        allTaxRatesLoader,
        enabledCurrenciesLoader,
        productsLoader,
        displayNotification
      ]
    )

    return load
  }
